import React, { ReactNode, Suspense, useEffect, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Spin } from 'antd'
import { useIdentity } from '@/hooks/auth/useIdentity'

import { Footer, Layout, OffLayoutArea, Sider } from '@components/layout'
import { SiderContext } from '@components/layout/sider/context'
import { ChangelogModalProvider, WelcomeModal } from '@features/changelog'
import { OnboardingTour } from '@features/onboarding'
// Deep import, not the hub barrel: AppShell is eager, and the barrel would drag
// the lazily-routed SalesmanSynthesisStep tree into the initial bundle.
import { HubScaleProvider } from '@features/projects/hub/HubScaleProvider'
import { isProjectHubRole } from './utils/projectHubRole'
import type { Account } from 'types'

const SiderStateManager: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState<boolean>(true)
  const { data: identity } = useIdentity<Account>()
  // Project-hub roles (SALESMAN + SOCIETY) get the menu expanded by default —
  // the design-system mock for the hub sidebar always shows labels (#321).
  // Other roles keep the legacy collapsed-by-default behavior. Apply the flip
  // exactly once when the identity resolves so user-driven collapses stick.
  const didExpandForHubRole = useRef(false)
  useEffect(() => {
    if (
      !didExpandForHubRole.current &&
      isProjectHubRole(identity?.account_type)
    ) {
      didExpandForHubRole.current = true
      setCollapsed(false)
    }
  }, [identity?.account_type])
  return (
    <SiderContext.Provider value={{ collapsed, setCollapsed }}>
      {children}
    </SiderContext.Provider>
  )
}

const PageFallback: React.FC = () => (
  <div
    style={{
      display: 'flex',
      minHeight: '60vh',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spin size="large" />
  </div>
)

export const AppShell: React.FC = () => {
  return (
    <ChangelogModalProvider>
      <SiderStateManager>
        <Layout
          Footer={Footer}
          Sider={Sider}
          OffLayoutArea={OffLayoutArea}
        >
          {/* Hub roles read the routed content one notch larger; the sider
              and header are rendered by Layout outside this subtree and keep
              the base scale. */}
          <HubScaleProvider>
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </HubScaleProvider>
        </Layout>
        <OnboardingTour />
      </SiderStateManager>
      <WelcomeModal />
    </ChangelogModalProvider>
  )
}
